// @file Shared view-as preview pill fill/outline derivation for the board banner and access gate.
import { shadeColor, type ColorString, type RGBString } from '@@/bits/color'
import { getSurfaceAccentColors } from '@@/bits/surface_colors'
import type { WallBackground } from '@@/types'

type ViewAsPreviewBackground = Partial<
  Pick<
    WallBackground,
    | 'primary_color_as_rgb'
    | 'dominant_color_as_rgb'
    | 'accent_color_as_rgb'
    | 'accent_text_color'
    | 'is_custom'
    | 'type'
    | 'luminance_name'
  >
>

interface ViewAsPreviewPillColors {
  backgroundColor: string | undefined
  textColor: string | undefined
}

/**
 * Primary-variant color fed into the pill accent resolution. Mirrors the surface store getter
 * `skinPrimaryVariantColorAsRGB` (vuexstore/surface/index.ts) exactly — including shading a
 * primary-less solid-color background darker when its luminance is anything but light — so the
 * board and gate resolve the same accent for the same wall.
 */
function getViewAsPreviewPrimaryVariantColor(background: ViewAsPreviewBackground): string | null {
  if (background.primary_color_as_rgb != null) return background.primary_color_as_rgb
  const dominant = background.dominant_color_as_rgb
  if (background.type === 'solid_color' && dominant != null) {
    return shadeColor(dominant, background.luminance_name === 'light' ? 25 : -10)
  }
  return dominant ?? null
}

interface ViewAsPreviewPillColorInput {
  primaryVariantColorAsRGB?: RGBString | string | null
  accentColorAsRGB?: ColorString | string | null
  accentTextColor?: 'white' | 'black' | null
  isMap: boolean
  isCustomBackground: boolean
}

interface ViewAsPreviewPillResolvedColors {
  backgroundColor: ColorString | string | undefined
  textColor: 'white' | 'black' | string | undefined
}

/**
 * Resolves the accent pair the pill paints with. Mirrors the board banner's `previewColors`:
 * without a primary variant there is no custom-wallpaper mapping to run, so the theme accent
 * is returned directly; otherwise the shared surface accent contract handles map/preset/custom.
 */
async function resolveViewAsPreviewPillAccent(
  input: ViewAsPreviewPillColorInput,
): Promise<ViewAsPreviewPillResolvedColors> {
  if (!input.primaryVariantColorAsRGB) {
    return {
      backgroundColor: input.accentColorAsRGB ?? undefined,
      textColor: input.accentTextColor ?? undefined,
    }
  }
  return await getSurfaceAccentColors({
    backgroundColorInRgb: input.primaryVariantColorAsRGB as RGBString,
    accentBackgroundColor: (input.accentColorAsRGB ?? undefined) as ColorString | undefined,
    accentTextColor: (input.accentTextColor ?? undefined) as 'white' | 'black' | undefined,
    isMap: input.isMap,
    isCustomWallpaper: input.isCustomBackground,
  })
}

/**
 * Custom wallpapers resolve through a dynamic import; while that map is pending the initial
 * accent can be nil. Hold paint instead of falling through to black so the pill does not flash.
 */
function getViewAsPreviewPillBackgroundColor(
  resolved: ViewAsPreviewPillResolvedColors,
  isCustomBackground: boolean,
): string | undefined {
  if (resolved.backgroundColor != null) return resolved.backgroundColor as string
  if (isCustomBackground) return undefined
  return 'rgb(0, 0, 0)'
}

function getViewAsPreviewPillTextColor(
  resolved: ViewAsPreviewPillResolvedColors,
  isCustomBackground: boolean,
): string | undefined {
  if (resolved.textColor != null) return resolved.textColor as string
  if (isCustomBackground && resolved.backgroundColor == null) return undefined
  return 'white'
}

function getViewAsPreviewPillPaintStyle(
  backgroundColor: string | undefined,
  textColor: string | undefined,
): { opacity: 0 } | { backgroundColor: string; color: string | undefined } {
  if (backgroundColor == null) return { opacity: 0 as const }
  return { backgroundColor, color: textColor }
}

function getViewAsPreviewPillOutlineStyle(
  backgroundColor: string | undefined,
): { opacity: 0 } | { borderColor: string } {
  if (backgroundColor == null) return { opacity: 0 as const }
  return { borderColor: backgroundColor }
}

/** Resolves pill fill colours for a wall background and map flag (board banner + access gate). */
async function viewAsPreviewPillColors(
  background: ViewAsPreviewBackground | null | undefined,
  isMap: boolean,
): Promise<ViewAsPreviewPillColors> {
  const wallBackground = background ?? {}
  const isCustomBackground = wallBackground.is_custom ?? false
  const resolved = await resolveViewAsPreviewPillAccent({
    primaryVariantColorAsRGB: getViewAsPreviewPrimaryVariantColor(wallBackground),
    accentColorAsRGB: wallBackground.accent_color_as_rgb,
    accentTextColor: wallBackground.accent_text_color,
    isMap,
    isCustomBackground,
  })
  return {
    backgroundColor: getViewAsPreviewPillBackgroundColor(resolved, isCustomBackground),
    textColor: getViewAsPreviewPillTextColor(resolved, isCustomBackground),
  }
}

export {
  getViewAsPreviewPillOutlineStyle,
  getViewAsPreviewPillPaintStyle,
  getViewAsPreviewPrimaryVariantColor,
  viewAsPreviewPillColors,
}
export type { ViewAsPreviewBackground, ViewAsPreviewPillColors }
